import { type RefObject, useCallback, useEffect, useState } from "react";

import type { Confidence } from "@memorang/types";
import type {
	AIResponsePayload,
	BaseItemSchema,
} from "@memorang/types/src/session/new-session";
import type { SessionState } from "../../hooks/types";
import { useAilaStore } from "../../hooks/useAilaStore";
/* -----------------Hooks--------------- */
import useSessionStore from "../../hooks/useSessionStore";

import { events } from "@constants/tracking";
import { useDailyProgressStore } from "@features/dashboard";
import { useExamContext } from "@features/exam";
import {
	type ProethicsScenarioId,
	computeScenarioRubricScore,
	useProethicsFlowStore,
} from "@features/proethics-simulation";
import { buildSimulationAiResponse } from "@features/proethics-simulation/helpers/summativePayload";
import { useEMRStore } from "@features/session/components/emr-card/useEMRStore";
import {
	AI_SUBMITTABLE_VARIANTS,
	MAPPED_ITEM_TYPES,
} from "@features/session/constants";
import { isAiSimulationItem } from "@features/session/helpers";
import { getTotalScoreForAiItem } from "@features/session/helpers/aiFeedback";
import { useProethicaSessionStore } from "@features/session/hooks/useProethicaSessionStore";
import { isWeb } from "@helpers/platform";
import useAnalytics from "@hooks/useAnalytics";
import useClerkUser from "@hooks/useClerkUser";
import { Box } from "@memorang/ui";
import { router, useLocalSearchParams } from "expo-router";
import { useTranslation } from "react-i18next";
import DialogSession from "../../components/SessionDialog";
import SessionEndDialog from "../../components/SessionEndDialog";
/* -----------------Components--------------- */
import StudySessionBottomAppBar from "../../components/StudySessionBottomAppBar";
import type { DialogType } from "../../constants/dialog-configs";
import useAIFeedbackStore from "../../hooks/useAIFeedbackStore";
import { useSession } from "../../hooks/useSession";
import { recordSummativeTestEvents } from "../../relay/RecordSummativeTestEvents";
import {
	calculateAndUpdateProgress,
	getNextButtonStates,
	getShouldShowConfidencesSubmitButtons,
	getShowButtonNavFinish,
	getShowSubmitAnswerAIFeedbackButton,
	handlePostAnswer,
	handleSendSummativeAnswerEvent,
	hasAnySummativeSelection,
	recordScenarioAnswerEvent,
	sessionHasAnswerableItems,
} from "./helpers";

const recordScenarioSummativeResult = ({
	state,
	simulationItem,
	scenarioId,
	userId,
	examId,
}: {
	state: SessionState;
	simulationItem: BaseItemSchema;
	scenarioId?: ProethicsScenarioId;
	userId?: number;
	examId?: string;
}) => {
	const flow = useProethicsFlowStore.getState();
	const run =
		scenarioId && flow.scenarioId !== scenarioId ? flow.runs[scenarioId] : flow;
	const summative = run?.summative;
	const reflectSkills = simulationItem.content?.simulation?.reflectSkills ?? [];
	const { earned, percentage, hasScore } = computeScenarioRubricScore(
		summative,
		reflectSkills,
	);
	if (!hasScore) {
		return;
	}
	const aiResponse: AIResponsePayload = buildSimulationAiResponse({
		summative,
		reflectSkills,
		threadId: run?.threadId,
		transcript: run?.transcript ?? [],
	});
	recordScenarioAnswerEvent({
		state,
		item: simulationItem,
		variant: simulationItem.metadata?.taskType ?? "AI_SIMULATION",
		score: percentage,
		points: earned,
		userId,
		examId,
		aiResponse,
	});
};

// Next-button label/tooltip under the active gates: an active simulation and a
// proethica test item without a selection both pin the label to "Next" (never
// "Skip") and explain, on hover, why the button is disabled.
const getNextButtonPresentation = ({
	simulationGate,
	answerGate,
	streaming,
	nextButtonText,
	nextButtonDisabledTooltip,
	t,
}: {
	simulationGate: boolean;
	answerGate: boolean;
	streaming: boolean;
	nextButtonText: string;
	nextButtonDisabledTooltip?: string;
	t: ReturnType<typeof useTranslation>["t"];
}) => {
	const text =
		streaming || answerGate ? t("session.navigation.next") : nextButtonText;
	if (simulationGate) {
		return {
			text,
			tooltip:
				"The conversation is still active. End the conversation to continue.",
		};
	}
	if (answerGate) {
		return { text, tooltip: "Select an answer to continue." };
	}
	return { text, tooltip: nextButtonDisabledTooltip };
};

interface Props {
	// biome-ignore lint/suspicious/noExplicitAny: flashlistRef generic typing override
	flashlistRef?: RefObject<any>;
}
const StudySessionBottombarContainer = ({ flashlistRef }: Props) => {
	const state = useSessionStore((state) => state);

	const { index } = useLocalSearchParams<{
		index: string;
	}>();

	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const {
		updateCurrentItemIndex,
		sessionItems,
		id,
		inReviewMode,
		isSummativeTest,
		currentBlockIndex,
		timeElapsed,
		sessionTime,
		updateProgress,
		numGoal,
		updateCurrentBlockIndex,
		numericId,
		toggleShowBreakDialog,
		answers,
	} = state;

	const { endCurrentSession } = useSession();

	const { explicitCallTrackCustomEvent } = useAnalytics();

	const hasAnsweredAtLeastOneItem = answers ? answers.size > 0 : false;
	const hasSummativeSelection = hasAnySummativeSelection(state);
	const hasNoAnswerableItems = !sessionHasAnswerableItems(state);

	const [startTime, setStartTime] = useState<Date>(new Date());
	const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);

	const toggleShowEndSessionDialog = () => {
		setShowEndSessionDialog((prev) => !prev);
	};

	const dailyProgressStore = useDailyProgressStore();

	const [showSessionDialogData, setShowSessionDialogData] = useState<{
		show: boolean;
		type?: DialogType;
	}>({
		show: false,
	});

	const {
		currentExamContext: { examId },
	} = useExamContext();

	const updateShowDrawer = useAilaStore((state) => state.updateShowDrawer);

	const breaks = useSessionStore((state) => state.breaks);

	const lastBreak = breaks?.length ? breaks[breaks.length - 1] : null;

	const isBreakExpired = lastBreak?.status === "EXPIRED";

	const isBreakOngoing = useSessionStore((state) => state.isBreakOngoing);

	const currentSessionItems = sessionItems;
	const currentItem = currentSessionItems[currentItemIndex];

	const itemType = currentItem.metadata?.type;
	const mappedItemType = MAPPED_ITEM_TYPES[itemType!];

	const currentItemId = currentItem?.id!;
	const maximumScore =
		currentItem.response?.maximumScore ?? getTotalScoreForAiItem(itemType!);
	const isAiSubmittable = AI_SUBMITTABLE_VARIANTS.includes(mappedItemType);

	const { userPublicMetadataId } = useClerkUser();

	const slideshowDurationInSeconds = useAIFeedbackStore(
		(state) => state.slideshowDurationInSecondsByItemId[currentItemId],
	);

	const handleUpdateItemStatePeriodically = useCallback(
		(time: number) => {
			const itemState = {
				sessionId: `${id}`,
				sessionTime: time,
				itemId: `${currentItemId}`,
				userId: String(userPublicMetadataId),
				variant: itemType!,
			};
			recordSummativeTestEvents(itemState);
		},
		[currentItemId, id, userPublicMetadataId, itemType],
	);

	useEffect(() => {
		if (
			timeElapsed &&
			timeElapsed % 300 === 0 &&
			!inReviewMode &&
			isSummativeTest &&
			!isBreakOngoing
		) {
			handleUpdateItemStatePeriodically(timeElapsed + (sessionTime || 0));
		}
	}, [
		handleUpdateItemStatePeriodically,
		inReviewMode,
		isSummativeTest,
		sessionTime,
		timeElapsed,
		isBreakOngoing,
	]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	useEffect(() => {
		setStartTime(new Date());
	}, [currentItemId]);

	const showConfidenceSubmitButtons =
		getShouldShowConfidencesSubmitButtons(state);

	// next button states
	const {
		text: nextButtonText,
		disabled: disableNextButton,
		hasNotAnswered,
		disabledNextTooltip: nextButtonDisabledTooltip,
	} = getNextButtonStates(state, currentItemIndex);
	const showButtonNavFinish = getShowButtonNavFinish(state, currentItemIndex);

	const sessionLength = sessionItems.length;
	const isLastItem = currentItemIndex === sessionLength - 1;

	const { t } = useTranslation();

	const prevButtonText = t("session.navigation.previous");

	const showPreviousButton = currentItemIndex > 0;

	const isLastBlock = isSummativeTest
		? currentBlockIndex === sessionItems?.length - 1
		: false;

	const isLastItemInTheBlock = false;

	useEffect(() => {
		if (currentItemIndex === 0) {
			const initProgress = 1 / numGoal!;
			updateProgress(initProgress);
		}
	}, [currentItemIndex, numGoal, updateProgress]);
	const handleUpdateAnswers = (confidence: Confidence) => {
		handlePostAnswer(
			state,
			startTime,
			confidence,
			dailyProgressStore,
			examId,
			mappedItemType,
			userPublicMetadataId,
			!isAiSubmittable,
			undefined,
			undefined,
			slideshowDurationInSeconds,
		);
	};

	const handleTrackAnswerEventAi = (confidence: Confidence) => {
		const aiResponse =
			useAIFeedbackStore.getState().aiResponse?.[currentItemId];
		handlePostAnswer(
			state,
			startTime,
			confidence,
			dailyProgressStore,
			examId,
			mappedItemType,
			userPublicMetadataId,
			true,
			aiResponse,
			maximumScore,
			slideshowDurationInSeconds,
		);
	};

	const onConfidenceProvided = (confidence: Confidence) => {
		handleUpdateAnswers(confidence);
	};

	const handlePrevious = () => {
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemNavigated,
			sessionId: id,
			itemId: currentItemId,
			type: "previous",
		});
		router.setParams({
			index: String(currentItemIndex),
			prev: "true",
		});
		const previousIndex = currentItemIndex - 1;
		updateCurrentItemIndex(previousIndex);
		useProethicaSessionStore
			.getState()
			.setResumeSessionIndex(numericId, previousIndex);
		calculateAndUpdateProgress(state, false);
	};

	useEffect(() => {
		if (!isWeb) {
			flashlistRef?.current?.scrollToIndex({
				index: currentItemIndex,
				animated: true,
			});
		}
	}, [currentItemIndex, flashlistRef]);

	// ProEthics simulation gating: while the AI_SIMULATION item is current, Next
	// stays disabled until the learner ends the conversation. Ending freezes the
	// transcript; Next then fires the summative submit for the reflect step.
	const hasEndedSimulation = useProethicsFlowStore((s) => s.hasEnded);
	const submitSummative = useProethicsFlowStore((s) => s.submitSummative);
	const isSimulationItem = isAiSimulationItem(sessionItems[currentItemIndex]);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const handleNext = () => {
		// Leaving the simulation: kick off the summative so the reflect step can
		// render the report (guarded against double submit inside the store).
		// Skipped in review mode: hydrated runs already have hasSubmitted, so the
		// resolved promise would re-record the answer event on every Next press.
		if (isSimulationItem && !inReviewMode) {
			const simulationItem = currentItem;
			const scenarioId = simulationItem.metadata?.scenarioId as
				| ProethicsScenarioId
				| undefined;
			// The summative streams in asynchronously; once it finalizes, record the
			// scenario's AI-rubric result as an answer event.
			submitSummative().then(() =>
				recordScenarioSummativeResult({
					state,
					simulationItem,
					scenarioId,
					userId: userPublicMetadataId,
					examId,
				}),
			);
		}
		const eventParams = {
			index: currentItemIndex,
			itemId: currentItemId,
			sessionId: id,
			inReviewMode: String(inReviewMode),
		};
		if (isLastItemInTheBlock && !inReviewMode && isSummativeTest) {
			updateShowDrawer(false);
			const type = isLastBlock ? "completeTest" : "completeSection";
			if (isSummativeTest && !inReviewMode) {
				handleSendSummativeAnswerEvent(
					state,
					currentItemIndex,
					String(userPublicMetadataId),
					startTime,
				);
			}
			if (isBreakExpired) {
				setShowSessionDialogData({
					show: true,
					type,
				});
			} else {
				toggleShowBreakDialog(true);
			}
			return;
		}
		if (showButtonNavFinish || isLastItem) {
			updateShowDrawer(false);
			explicitCallTrackCustomEvent({
				eventName: events.sessionFinishNavClicked,
				...eventParams,
			});
			if (inReviewMode) {
				if (router.canGoBack()) {
					router.back();
				} else {
					router.replace("/home");
				}
			} else if (
				hasAnsweredAtLeastOneItem ||
				hasSummativeSelection ||
				hasNoAnswerableItems
			) {
				endCurrentSession({
					id: id,
					userId: userPublicMetadataId!,
				});
				explicitCallTrackCustomEvent({
					eventName: events.sessionItemNavigated,
					...eventParams,
					type: "finish",
				});
				router.replace({
					pathname: "/(protected)/session/[id]/report",
					params: {
						id: id,
					},
				});
			} else {
				toggleShowEndSessionDialog();
			}
			return;
		}

		const newIndex = currentItemIndex + 1;

		router.setParams({
			index: String(newIndex + 1),
			prev: "false",
		});
		updateCurrentItemIndex(newIndex);
		useProethicaSessionStore
			.getState()
			.setResumeSessionIndex(numericId, newIndex);
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemNavigated,
			...eventParams,
			type: "next",
		});
		if (isSummativeTest && !inReviewMode) {
			handleSendSummativeAnswerEvent(
				state,
				currentItemIndex,
				String(userPublicMetadataId),
				startTime,
			);
		}
		calculateAndUpdateProgress(state, true);
	};

	const progressText = isSummativeTest
		? ""
		: `ITEM ${currentItemIndex + 1} / ${sessionItems.length}`;

	const handleCloseDialog = () => {
		setShowSessionDialogData({
			show: false,
		});
	};
	const moveToNextBlock = () => {
		if (isLastBlock) {
			endCurrentSession({
				id: id,
				userId: userPublicMetadataId!,
			});
			router.replace({
				pathname: "/(protected)/session/[id]/report",
				params: {
					id: numericId,
				},
			});
		} else {
			router.setParams({
				index: "1",
			});
			updateCurrentItemIndex(1);
			updateCurrentBlockIndex(currentBlockIndex + 1);
		}
	};
	const handleClickActionButton = (type: string) => {
		if (type === "cancel") {
			handleCloseDialog();
		} else {
			handleCloseDialog();
			moveToNextBlock();
		}
	};

	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const audioResponse = useAIFeedbackStore((state) => state.audioResponse);
	const streaming = useAIFeedbackStore((state) => state.streaming);
	const { show: showSubmitAnswerAIFeedbackButton } =
		getShowSubmitAnswerAIFeedbackButton({
			sessionState: state,
			currentItemIndex,
			currentUserResponse: userResponse?.[currentItemId],
			currentAudioResponse: audioResponse?.[currentItemId],
			shouldValidateAudio: false,
		});

	const handleNavigation = (type: "positive" | "negative") => {
		if (type === "positive") {
			useProethicaSessionStore
				.getState()
				.setResumeSessionIndex(numericId, currentItemIndex);
			router.replace("/home");
		} else {
			toggleShowEndSessionDialog();
		}
	};

	const handleTrackConfidenceClick = (confidence: Confidence) => {
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemConfidenceClicked,
			confidence,
			itemId: currentItemId,
			sessionId: id,
		});
	};

	const isSubmittingEMRData = useEMRStore((state) => state.isSubmittingEMRData);
	// Questions cannot be skipped: while an answerable item has no selection,
	// Next stays disabled and shows the "select an answer" tooltip.
	const answerGate = hasNotAnswered;
	const nextButtonPresentation = getNextButtonPresentation({
		simulationGate: isSimulationItem && !hasEndedSimulation,
		answerGate,
		streaming,
		nextButtonText,
		nextButtonDisabledTooltip,
		t,
	});
	return (
		<Box maxWidth={"100%"} alignSelf="center" width="100%">
			<StudySessionBottomAppBar
				handleNext={handleNext}
				disableNextButton={
					disableNextButton ||
					streaming ||
					isSubmittingEMRData ||
					(isSimulationItem && !hasEndedSimulation)
				}
				disabledNextTooltip={nextButtonPresentation.tooltip}
				nextButtonText={nextButtonPresentation.text}
				handlePrevious={handlePrevious}
				handleTrackConfidenceClick={handleTrackConfidenceClick}
				disablePreviousButton={streaming}
				showSubmitAnswerAIFeedbackButton={showSubmitAnswerAIFeedbackButton}
				isSummativeTest={isSummativeTest}
				showButtonPrevious={showPreviousButton}
				prevButtonText={prevButtonText}
				showButtonNavFinish={showButtonNavFinish}
				onConfidenceProvided={onConfidenceProvided}
				showConfidenceSubmitButtons={Boolean(showConfidenceSubmitButtons)}
				progressText={progressText}
				onAiFeedbackProvided={handleTrackAnswerEventAi}
			/>

			{showSessionDialogData.show && (
				<DialogSession
					type={showSessionDialogData.type}
					handleClickButton={handleClickActionButton}
					handleClose={handleCloseDialog}
					open={showSessionDialogData.show}
				/>
			)}

			{showEndSessionDialog && (
				<SessionEndDialog
					visible={showEndSessionDialog}
					onDismiss={toggleShowEndSessionDialog}
					handleAction={handleNavigation}
					showProgressMessage={false}
				/>
			)}
		</Box>
	);
};

export default StudySessionBottombarContainer;
